import { Building2, Check } from 'lucide-react';
import { usePropertyContext } from '@/contexts/PropertyContext';

/**
 * Full-page property selection screen, shown when multiple properties
 * exist and no property has been selected yet. Mirrors the HotelKey
 * "Available Properties" landing page pattern.
 */
export function PropertySelector() {
  const { properties, selectedProperty, selectProperty, isLoading } = usePropertyContext();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div
          className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"
          role="status"
          aria-label="Loading properties"
        />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-12">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Select Property</h1>
      <p className="text-gray-500 mb-8">Choose a property to manage</p>

      <div className="space-y-3" role="radiogroup" aria-label="Available properties">
        {properties.map((property) => {
          const isSelected = selectedProperty?.id === property.id;
          return (
            <button
              key={property.id}
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => selectProperty(property)}
              className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 ${
                isSelected
                  ? 'border-primary-600 bg-primary-50'
                  : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center">
                <Building2 size={24} className="text-primary-600" aria-hidden="true" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900">{property.name}</div>
                {property.code && (
                  <div className="text-sm text-gray-500 font-mono">{property.code}</div>
                )}
                {property.address && (
                  <div className="text-sm text-gray-400 truncate">{property.address}</div>
                )}
              </div>
              {isSelected && (
                <Check size={20} className="text-primary-600 flex-shrink-0" aria-hidden="true" />
              )}
            </button>
          );
        })}
      </div>

      {properties.length === 0 && (
        <div className="text-center py-12">
          <Building2 size={48} className="mx-auto text-gray-300 mb-4" aria-hidden="true" />
          <h3 className="text-lg font-semibold text-gray-600">No Properties Available</h3>
          <p className="text-sm text-gray-400 mt-1">
            Create a property in the Properties section first.
          </p>
        </div>
      )}
    </div>
  );
}
